import axios, { AxiosRequestConfig, Axios, AxiosError } from 'axios';
import { ACCESS_TOKEN_STORAGE_KEY, useOptionalAuthContext } from '../context/AuthContext';
import { HOST } from '../config/client';
import { usePopups } from '../context/PopupContext';
import storage from '../storage/storage';
import { Capacitor } from '@capacitor/core';
import {
  ANDROID_APP_VERSION_HEADER,
  IOS_APP_VERSION_HEADER,
  MOBILE_APP_NAME_HEADER,
  OTA_BUNDLE_VERSION_HEADER,
  REQUEST_SOURCE_HEADER,
  REQUEST_SOURCES,
} from '../config/http';
import { ERROR_CODES, ErrorCode } from '../config/errorCodes';
import {
  ERROR_MESSAGES,
  GENERIC_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
  SERVER_ERROR_MESSAGE,
} from '../config/errorMessages';
import { App } from '@capacitor/app';
import { OTA_BUNDLE_VERSION } from '../config/otaBundle';
import { OTA_UPDATE_PAGE_PATH, reactToOtaBundleTooLowOnWeb } from '../utils/otaUtils';
import { APP_NAME_CODE } from '../config/mobileApps';
import { APP_UPDATE_PAGE_PATH } from '../config/appUpdate';
import { isAuthError } from '../utils/authErrors';

const AXIOS_METHODS = ['get', 'put', 'post', 'patch', 'delete'];

const isNativePlatform = Capacitor.isNativePlatform();
const isAndroid = Capacitor.getPlatform() === 'android';
const isIOS = Capacitor.getPlatform() === 'ios';

export interface NotificationMessage {
  summary: string;
  detail: string;
}

export type RequestErrorMessages = {
  summary: string;
  /** Shown when nothing more specific matches; without it the generic text of the status is shown */
  defaultDetail?: string;
  [key: number]: string;
} & { [key in ErrorCode]?: string };

export interface RequestFeedbackMessages {
  successMessage: NotificationMessage;
  errorMessages: RequestErrorMessages;
}

interface ServerResponse<T> {
  data?: T;
  message?: string;
  success: boolean;
}

type StatusHandler = (...args: any) => void;
type StatusHandlers = {
  defaultError?: StatusHandler;
  defaultSuccess?: StatusHandler;
  [key: number]: StatusHandler;
} & { [key in ErrorCode]?: StatusHandler };

interface ClientRequestConfig extends AxiosRequestConfig<any> {
  body?: any;
  errorMessages?: RequestErrorMessages;
  successMessage?: NotificationMessage;
  handlers?: StatusHandlers;
}

interface ClientOptions {
  /**
   * Requests reject with the error instead of resolving with `undefined`, so the caller can tell a network error or a
   * 5xx from an auth error (`isAuthError`). Auth errors also call the context `signOut` when there is one (not inside
   * `AuthProvider`, where the caller must sign out). The version redirects and the web reload never settle.
   */
  throwOnError?: boolean;
}

type AxiosRequest = Axios['get'] | Axios['put'] | Axios['post'] | Axios['patch'] | Axios['delete'];
export type ClientRequest = <T>(url: string, config?: ClientRequestConfig | undefined) => Promise<T | undefined>;
type Client = {
  get: ClientRequest;
  put: ClientRequest;
  post: ClientRequest;
  patch: ClientRequest;
  delete: ClientRequest;
};

/**
 * Axios has no timeout by default: on a stalled connection (captive portal, half-open socket, a hung backend) a request never
 * ends. 60 s leaves room for the slowest routes (business analytics, p95 18.9 s). An upload (a `FormData` body) keeps no
 * timeout, as before: a file on a slow connection can take longer. A call can set its own `timeout` (`0` turns it off).
 */
export const DEFAULT_REQUEST_TIMEOUT_MS = 60_000;

const HTTP_FORBIDDEN = 403;
const HTTP_SERVER_ERROR = 500;

// The code and the status come from the server: a key such as "constructor" finds a function on the object, which is no text
const textFor = (texts: Readonly<Record<string, unknown>>, key: string | number | undefined): string | undefined => {
  const text = key === undefined ? undefined : texts[key];
  return typeof text === 'string' ? text : undefined;
};

/**
 * The detail of the error toast, the first that exists: the text of the call for the code, the catalog for the code, the text
 * of the call for the status, the offline text, the default detail of the call, and a generic text by status. The `message`
 * of the backend is never shown.
 */
const getErrorDetail = (err: AxiosError, errorMessages: RequestErrorMessages): string => {
  const code = (err.response?.data as { code?: string } | null | undefined)?.code;
  const status = err.status;
  const generic =
    status === HTTP_FORBIDDEN
      ? ERROR_MESSAGES[ERROR_CODES.INSUFFICIENT_PERMISSIONS]
      : status !== undefined && status >= HTTP_SERVER_ERROR
        ? SERVER_ERROR_MESSAGE
        : GENERIC_ERROR_MESSAGE;
  return (
    textFor(errorMessages, code) ??
    textFor(ERROR_MESSAGES, code) ??
    textFor(errorMessages, status) ??
    (err.response ? undefined : NETWORK_ERROR_MESSAGE) ??
    errorMessages.defaultDetail ??
    generic
  );
};

export const useClient = ({ throwOnError = false }: ClientOptions = {}) => {
  const { showToast } = usePopups();
  // Null inside AuthProvider, which uses this hook for the session requests.
  const signOut = useOptionalAuthContext()?.signOut;

  const client: Client = AXIOS_METHODS.reduce((acc, method) => {
    return {
      ...acc,
      [method]: async <T,>(url: string, config?: ClientRequestConfig | undefined) => {
        const axiosMethod = axios[method as keyof Axios] as AxiosRequest;
        const { errorMessages, successMessage, handlers, body, ...restConfig } = config ?? {};

        const requestConfig: AxiosRequestConfig = {
          ...restConfig,
          timeout: restConfig.timeout ?? (body instanceof FormData ? 0 : DEFAULT_REQUEST_TIMEOUT_MS),
          headers: {
            ...restConfig?.headers,
            Authorization: `Bearer ${await storage.getItem(ACCESS_TOKEN_STORAGE_KEY)}`,
            [REQUEST_SOURCE_HEADER]: isNativePlatform ? REQUEST_SOURCES.MOBILE_APP : '',
            [MOBILE_APP_NAME_HEADER]: APP_NAME_CODE,
            [OTA_BUNDLE_VERSION_HEADER]: String(OTA_BUNDLE_VERSION),
          },
          withCredentials: true,
        };
        const finalUrl = `${HOST}${url}`;

        if (isNativePlatform && requestConfig.headers) {
          const appInfo = await App.getInfo();
          if (isAndroid) {
            requestConfig.headers[ANDROID_APP_VERSION_HEADER] = appInfo.version;
          }
          if (isIOS) {
            requestConfig.headers[IOS_APP_VERSION_HEADER] = appInfo.version;
          }
        }

        try {
          const { data: serverResponse, status } =
            method === 'get' || method === 'delete'
              ? await axiosMethod<ServerResponse<T>>(finalUrl, requestConfig)
              : await axiosMethod<ServerResponse<T>>(finalUrl, body || {}, requestConfig);

          if (successMessage) {
            showToast?.({
              severity: 'success',
              summary: successMessage.summary,
              detail: successMessage.detail,
            });
          }

          if (handlers?.[status]) {
            handlers[status]();
          } else {
            handlers?.defaultSuccess?.();
          }

          return serverResponse.data;
        } catch (err) {
          if (err instanceof AxiosError) {
            console.error(err);

            if (isAuthError(err)) {
              await signOut?.();
              if (throwOnError) throw err;
              return;
            }
            if (err.response?.data?.code === ERROR_CODES.APP_VERSION_TOO_LOW) {
              if (window.location.pathname !== APP_UPDATE_PAGE_PATH) {
                window.location.assign(APP_UPDATE_PAGE_PATH);
              }
              // Never settles, like the OTA case below: callers such as fetchUser must not sign the user out.
              return new Promise<never>(() => undefined);
            }
            if (err.response?.data?.code === ERROR_CODES.OTA_BUNDLE_VERSION_TOO_LOW) {
              // Read at call time, not the module constant: the platform switch of the integration harness changes per test.
              if (Capacitor.isNativePlatform()) {
                if (window.location.pathname !== OTA_UPDATE_PAGE_PATH) {
                  window.location.assign(OTA_UPDATE_PAGE_PATH);
                }
              } else {
                reactToOtaBundleTooLowOnWeb();
              }
              // Never settles: callers such as fetchUser must not treat this as a failed request and sign the user out
              return new Promise<never>(() => undefined);
            }

            if (errorMessages) {
              showToast?.({
                summary: errorMessages.summary,
                detail: getErrorDetail(err, errorMessages),
                severity: 'error',
              });
            }

            if (handlers?.[err.response?.data?.code]) {
              handlers[err.response?.data?.code]();
            } else if (err.status && handlers?.[err.status]) {
              handlers[err.status]();
            } else {
              handlers?.defaultError?.();
            }
          }
          if (throwOnError) throw err;
        }
      },
    };
  }, {} as Client);

  return client;
};
